// Spec 65 i18n-framework — calendar-date display formatting.
//
// Lives beside formatMoney for the same reason: storage carries a machine
// value (an ISO date-only string) and exactly ONE place turns it into a
// locale-formatted string. It moved here out of customer-web when the mobile
// app needed it — mobile had no formatter at all and was rendering the raw
// "2026-08-17" into its Turkish UI.
//
// ICU selects incompatible Turkish patterns when `year` is omitted: a true
// range relaxes the requested 2-digit padding ("29.4 – 3.5"), while an equal
// range collapses to a slash form ("17/11"). A numeric year is therefore not
// optional: measured output is stable within each locale across both branches
// (tr: "17.08.2026 – 21.08.2026" / "17.11.2026"; en-US:
// "8/17/2026 – 8/21/2026" / "11/17/2026"). Dots versus slashes remain
// locale-specific instead of changing with range equality. Range punctuation
// stays here rather than in JSX (ADR-006), including the Hermes fallback.
//
// timeZone is pinned to UTC: date-only strings ("2026-04-29") parse to UTC
// midnight per the ECMA-262 date-time string format, so formatting them in
// the viewer's local zone shifts the displayed day back by one anywhere
// west of Greenwich. The API's start_date/end_date are calendar dates, not
// instants — render them zone-independently.

const DATE_PARTS = {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  timeZone: "UTC",
} as const;

export function formatDateRange(locale: string, startIso: string, endIso: string): string {
  const formatter = new Intl.DateTimeFormat(locale, DATE_PARTS);
  const start = new Date(startIso);
  const end = new Date(endIso);
  if (typeof formatter.formatRange === "function") return formatter.formatRange(start, end);

  // SDK 52 Hermes supports format(), but not formatRange(). Keep the same
  // locale/UTC options and collapse equal dates. Both MVP locales use this
  // range separator; each endpoint retains Intl's own ordering and padding.
  if (start > end) throw new RangeError("Invalid date range");
  const startText = formatter.format(start);
  const endText = formatter.format(end);
  return startText === endText ? startText : `${startText}\u2009–\u2009${endText}`;
}

/** Single date-only ISO string ("2024-03-12" → "12.03.2024" tr; "03/12/2024"
 *  en) under the SAME UTC pin as formatRange above — calendar dates render
 *  zone-independently. */
export function formatDate(locale: string, iso: string): string {
  return new Intl.DateTimeFormat(locale, DATE_PARTS).format(new Date(iso));
}

/**
 * An instant (ISO timestamp, e.g. a snooze or completion moment) as a locale
 * date and time. Unlike formatDate it is NOT pinned to UTC: an instant is a
 * wall-clock moment, so it renders in the zone the caller passes — the
 * viewer's effective zone, which the app reads from GET /v1/me.
 */
export function formatDateTime(locale: string, iso: string, timeZone: string): string {
  return new Intl.DateTimeFormat(locale, { dateStyle: "medium", timeStyle: "short", timeZone }).format(new Date(iso));
}

/**
 * A 0–100 percentage as a locale-formatted string.
 *
 * `style: "percent"` rather than a number plus a hardcoded "%" BECAUSE THE SIGN
 * MOVES: Turkish writes %40, English 40%. Screens that concatenated the literal
 * shipped "60%" into a tr-TR UI sitting next to a correctly-formatted "%50" —
 * measured on /surveys against /onboarding, 2026-08-03. The formatter also
 * absorbs the decimal-mark difference (tr "0,5") and trims the false precision
 * an API string like "40.00" carries.
 *
 * Accepts the API's decimal STRING as well as a number; a value that does not
 * parse comes back untouched rather than as "NaN%".
 *
 *   formatPercent("tr-TR", 60)      → "%60"
 *   formatPercent("en-US", 60)      → "60%"
 *   formatPercent("tr-TR", "40.00") → "%40"
 */
export function formatPercent(locale: string, value: number | string): string {
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return String(value);
  return new Intl.NumberFormat(locale, {
    style: "percent",
    maximumFractionDigits: 1,
  }).format(n / 100);
}
